// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Alert, Link } from '@mui/material';
import { CSSProperties, ForwardedRef, forwardRef } from 'react';

interface Props {
  /**
   * A style object to apply to the alert.
   */
  style?: CSSProperties;

  /**
   * A message to display in the alert.
   */
  message: string;

  /**
   * A callback fired when the alert requests to be closed.
   */
  onClose?: () => void;
}

// The message is rendered inline before the standard support footer.
// Callers historically pass fragments without trailing punctuation
// ("An error occurred while uploading time data"), which run into the
// next sentence when concatenated. Append a period when the message
// doesn't already end with terminal punctuation so the rendered
// paragraph reads cleanly.
function terminate(message: string): string {
  return /[.!?]$/.test(message) ? message : `${message}.`;
}

/**
 * Renders a Material UI Alert component with a standard Hardfin support
 * message. This function also forwards a ref to the alert component.
 */
function SupportAlertRenderer(props: Props, ref: ForwardedRef<HTMLDivElement>) {
  return (
    <Alert
      ref={ref}
      severity="error"
      onClose={props.onClose}
      style={props.style}
    >
      {terminate(props.message)} Hardfin support has been notified. You can{' '}
      <Link color="inherit" fontWeight="bold" href="#hs-chat-open">
        chat with us
      </Link>
      , email{' '}
      <Link color="inherit" href="mailto:support@hardfin.com" fontWeight="bold">
        support@hardfin.com
      </Link>
      , or call{' '}
      <Link color="inherit" fontWeight="bold" href="tel:415-969-3100">
        (415) 969-3100
      </Link>{' '}
      if you have any questions.
    </Alert>
  );
}

/**
 * An alert that renders a message along with a standard Hardfin support
 * message.
 */
export const SupportAlert = forwardRef(SupportAlertRenderer);
